import {
  PERMISSIONS,
  SECTION_LABELS,
  SECTIONS,
  type Permission,
  type Section,
} from "@xop/dashboard-shared";

/**
 * The role-matrix permission grouping, extracted from `RolesPanel.tsx`. Pure
 * data derivation — unit-tested so a new `SECTIONS` entry or `admin:*`
 * permission stays correctly bucketed.
 */
export interface PermissionGroup {
  key: Section | NonSectionGroupKey;
  label: string;
  permissions: Permission[];
}

/**
 * Prefixes that are NOT top-nav sections but still own permissions, in the
 * order they trail the section groups.
 *
 * Every permission must land in exactly one group or it becomes ungrantable —
 * present in the matrix's data, absent from its UI, so an admin can never tick
 * it. That is a silent failure (nothing throws; the checkbox simply isn't
 * there), which is why `permissionGroups.test.ts` asserts the grouping COVERS
 * `PERMISSIONS` rather than only that each grouped key is real. Adding a
 * permission under a new prefix means adding the prefix here.
 */
const NON_SECTION_GROUPS = [
  // Not a section: there is no Releases tab. Release artifacts are curated in
  // System Admin ▸ Products & Releases, but the authority to DOWNLOAD a disk
  // image is held by people (sales) who have no System Admin access at all, so
  // it cannot live under `admin:`.
  { key: "releases", label: "Software Releases" },
  { key: "admin", label: "System Admin" },
] as const;

type NonSectionGroupKey = (typeof NON_SECTION_GROUPS)[number]["key"];

/**
 * Permissions grouped by their `<prefix>:` so the matrix doesn't look like a
 * flat checkbox dump. Section prefixes first, in nav order; the non-section
 * ones (`releases:`, `admin:`) trail so they read as visibly distinct.
 */
export const GROUPS: PermissionGroup[] = (() => {
  const forPrefix = (prefix: string): Permission[] =>
    PERMISSIONS.filter((p) => p.startsWith(`${prefix}:`)) as Permission[];

  const groups: PermissionGroup[] = SECTIONS.map((s) => ({
    key: s,
    label: SECTION_LABELS[s],
    permissions: forPrefix(s),
  }));

  for (const { key, label } of NON_SECTION_GROUPS) {
    groups.push({ key, label, permissions: forPrefix(key) });
  }
  return groups;
})();
